/** @jsxImportSource @emotion/react */
import React, { FC, useCallback, useEffect } from 'react';
import { TextFields } from '@zip/business-components';
import { Tooltip } from 'components';
import { useMerchantApplicationContext } from 'contexts/MerchantApplication.context';
import { Validators, logEvent, useSignUpFunctions } from 'utils';
import { useSignUpAISuggestionFunctions } from 'utils/useSignUpAISuggestionFunctions';
import * as sharedStyles from './SignUpForm.styles';
import * as styles from './BusinessInfoStep.styles';
import * as Icons from '@zip/icons';
import { SignUpStepKey } from '../signUpConfig';
import { SparkleLoader } from 'components/sign-up/SparkleLoader';
import { AISuggestionButtons } from 'components/sign-up/AISuggestionButtons';
import {
  BusinessInfoProps,
  PatchType,
  StepComponentProps,
} from 'types/interfaces/sign-up/MerchantApplication.types';
import { MappedError, isFieldError } from 'types/common/ApiHelpers';
import { useBusinessInfoFormik } from './useBusinessInfoFormik';
import { useAbnLookupFlow } from './Abn/useAbnLookupFlow';
import { normalizeAbn } from './Abn/useAbnUtils';
import { Constants, css } from 'global';
import _ from 'lodash';
import { PageAction } from 'enums';

export const BusinessInfoStepModule: FC<StepComponentProps> = ({
  provideSubmit,
  onError,
}) => {
  const {
    setStepData,
    getStepData,
    merchantApplicationId,
    aiSuggestions,
    setAISuggestionsInContext,
  } = useMerchantApplicationContext();

  const {
    checkExistingApplicationOrMerchantByAbn,
    abnLookUp,
    getApplicationFormOptions,
    abortRequests: abortSignUpRequests,
  } = useSignUpFunctions();

  const {
    getMerchantBusinessInfoAISuggestionsAsync,
    abortRequests: abortAISuggestionRequests,
  } = useSignUpAISuggestionFunctions();

  const getBusinessInfoStepData = useCallback(
    () => getStepData(SignUpStepKey.BusinessInfo),
    [getStepData]
  );

  const websiteUrl = getStepData(SignUpStepKey.SignUpInfo)?.websiteUrl;

  const businessInfo = getBusinessInfoStepData();

  const saveBusinessInfoDraft = useCallback(
    (nextValues: BusinessInfoProps) => {
      setStepData(
        SignUpStepKey.BusinessInfo,
        {
          ...nextValues,
          abn: normalizeAbn(nextValues.abn),
        },
        PatchType.SignUpInfo,
        true
      );
    },
    [setStepData]
  );

  const handleSubmitBusinessInfo = useCallback(
    (values: BusinessInfoProps) => {
      saveBusinessInfoDraft(values);
    },
    [saveBusinessInfoDraft]
  );

  const {
    formik,
    isAbnValid,
    submitAttempted,
    setSubmitAttempted,
    validateAbnField,
    markAllTouched,
  } = useBusinessInfoFormik({
    businessInfo,
    onSubmitBusinessInfo: handleSubmitBusinessInfo,
  });

  const handleError = useCallback(
    (error: MappedError) => {
      if (isFieldError(error)) {
        Object.entries(error.fieldErrors).forEach(([field, message]) => {
          formik.setFieldError(field, message);
          formik.setFieldTouched(field, true, false);
        });
        // Field errors are shown inline — do not forward to snackbar
        return;
      }
      onError?.(error);
    },
    [formik, onError]
  );

  const {
    abnErrors,
    resetAbnErrors,
    sparkleState,
    entityNameVisible,
    tradingNameVisible,
    industryTypeVisible,
    industryTypeOptions,
    aiSuggestedIndustryTypes,
    aiSuggestedTradingName,
    resetFormPreserveAbn,
    runAbnLookupIfNeeded,
    resetAbn,
  } = useAbnLookupFlow({
    formik,
    websiteUrl,
    merchantApplicationId,
    saveBusinessInfoDraft,
    checkExistingApplicationOrMerchantByAbn,
    abnLookUp,
    getApplicationFormOptions,
    getMerchantBusinessInfoAISuggestionsAsync,
    aiSuggestions,
    setAISuggestionsInContext,
    onApiError: handleError,
    abortSignUpRequests,
    abortAISuggestionRequests,
  });

  const debouncedRunAbnLookup = useCallback(
    _.debounce((abn: string) => {
      runAbnLookupIfNeeded(abn);
    }, 300),
    [runAbnLookupIfNeeded]
  );

  const handleResetAbn = useCallback(() => {
    debouncedRunAbnLookup.cancel();
    resetAbn();
    setSubmitAttempted(false);
  }, [resetAbn, setSubmitAttempted, debouncedRunAbnLookup]);

  const buildNextValues = useCallback(
    (patch: Partial<BusinessInfoProps>): BusinessInfoProps => ({
      ...formik.values,
      ...patch,
    }),
    [formik.values]
  );

  const handleTradingNameSuggestionSelect = useCallback(
    async (value: string, _: number) => {
      await formik.setFieldValue('tradingName', value, false);
      saveBusinessInfoDraft(buildNextValues({ tradingName: value }));
      logEvent(PageAction.merchantOnboardingUABusinessDetailsAITradingName, {
        merchantApplicationId,
      });
    },
    [buildNextValues, formik, saveBusinessInfoDraft]
  );

  const handleIndustryTypeSuggestionSelect = useCallback(
    async (value: string, index: number) => {
      await formik.setFieldValue('industryType', value, false);
      saveBusinessInfoDraft(buildNextValues({ industryType: value }));
      logEvent(PageAction.merchantOnboardingUABusinessDetailsAIIndustryType, {
        merchantApplicationId,
        selectedOption: index,
      });
    },
    [buildNextValues, formik, saveBusinessInfoDraft]
  );

  const handleAbnChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedRunAbnLookup.cancel();
      const raw = e.target.value;
      const cleanAbn = normalizeAbn(raw);

      if (cleanAbn.length > 11) {
        return;
      }

      const formatted = Validators.formatAbn(raw);

      await formik.setFieldValue('abn', formatted, false);

      const { isValid } = validateAbnField(
        raw,
        'change',
        resetFormPreserveAbn,
        resetAbnErrors
      );

      if (!formatted.trim()) {
        saveBusinessInfoDraft({
          abn: '',
          entityName: '',
          entityType: '',
          tradingName: '',
          tradingSince: undefined,
          industryType: '',
        });
        return;
      }

      if (!isValid) {
        return;
      }

      const nextValues: BusinessInfoProps = {
        ...formik.values,
        abn: cleanAbn,
      };

      saveBusinessInfoDraft(nextValues);
      debouncedRunAbnLookup(cleanAbn);
    },
    [
      resetAbnErrors,
      formik,
      resetFormPreserveAbn,
      saveBusinessInfoDraft,
      debouncedRunAbnLookup,
      validateAbnField,
    ]
  );

  const handleAbnBlur = useCallback(() => {
    formik.setFieldTouched('abn', true, false);
    validateAbnField(
      formik.values.abn,
      'blur',
      resetFormPreserveAbn,
      resetAbnErrors
    );
  }, [resetAbnErrors, formik, resetFormPreserveAbn, validateAbnField]);

  const handleEntityNameBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      formik.handleBlur(e);

      saveBusinessInfoDraft(buildNextValues({ entityName: e.target.value }));
    },
    [buildNextValues, formik, saveBusinessInfoDraft]
  );

  const handleTradingNameBlur = useCallback(
    async (e: React.FocusEvent<HTMLInputElement>) => {
      formik.handleBlur(e);

      saveBusinessInfoDraft(buildNextValues({ tradingName: e.target.value }));
    },
    [buildNextValues, formik, saveBusinessInfoDraft]
  );

  const handleIndustryTypeChange = useCallback(
    (e: { target: { value: any } }) => {
      logEvent(
        PageAction.merchantOnboardingUABusinessDetailsDropdownIndustryType,
        { merchantApplicationId }
      );

      const { value } = e.target;

      formik.setFieldTouched('industryType', true, false);
      formik.setFieldValue('industryType', value, false);

      saveBusinessInfoDraft({
        ...formik.values,
        industryType: value,
      });
    },
    [formik, saveBusinessInfoDraft]
  );

  useEffect(() => {
    logEvent(PageAction.merchantOnboardingPVABNAndBusinessDetails, {
      merchantApplicationId,
    });
  }, []);

  useEffect(() => {
    if (!provideSubmit) {
      return;
    }

    provideSubmit(async () => {
      setSubmitAttempted(true);

      const errors = await formik.validateForm();
      const { isValid } = validateAbnField(
        formik.values.abn,
        'blur',
        resetFormPreserveAbn,
        resetAbnErrors
      );

      if (Object.keys(errors).length > 0 || !isValid || abnErrors) {
        markAllTouched();
        return false;
      }

      await formik.submitForm();
      return true;
    }, handleError);
  }, [provideSubmit, formik, handleError]);

  const hasBlockingAbnErrors = Boolean(abnErrors) || Boolean(formik.errors.abn);

  const abnIsReadyToShowFields = isAbnValid && !hasBlockingAbnErrors;

  const showEntitySkeleton =
    abnIsReadyToShowFields && !entityNameVisible && sparkleState.isLoading;

  const showTradingSkeleton =
    abnIsReadyToShowFields && !tradingNameVisible && sparkleState.isLoading;

  const showIndustrySkeleton =
    abnIsReadyToShowFields && !industryTypeVisible && sparkleState.isLoading;

  const showEntityNameInput = entityNameVisible;
  const showTradingNameInput = tradingNameVisible;
  const showIndustryTypeInput = industryTypeVisible;

  return (
    <form>
      <TextFields.Outlined
        type="text"
        name="abn"
        label="Australian Business Number (ABN)"
        css={[sharedStyles.textfield, styles.abnInput]}
        className="fs-mask"
        fullWidth
        value={formik.values.abn}
        onChange={handleAbnChange}
        onBlur={handleAbnBlur}
        disabled={isAbnValid}
        error={
          (formik.touched.abn && Boolean(formik.errors.abn)) ||
          Boolean(abnErrors)
        }
        helperText={
          abnErrors ||
          Validators.renderFieldError(
            formik.touched.abn,
            formik.errors.abn,
            submitAttempted
          )
        }
        endIcon={
          formik.values.abn || isAbnValid || hasBlockingAbnErrors ? (
            <Icons.Close
              css={[css.icon(true, '15px', '15px'), sharedStyles.closeIcon]}
              onClick={handleResetAbn}
            />
          ) : (
            <Tooltip
              content={
                <p className="body4" css={styles.tooltipContent}>
                  An Australian Business Number (ABN) is a unique, 11-digit
                  number that verifies you as a registered business.
                </p>
              }
              css={sharedStyles.tooltip}
              variant="primary"
              placement="top"
              enterTouchDelay={20}
              leaveTouchDelay={3000}
              maxWidth={240}
              arrow
              dense
            >
              <Icons.InformationCircle />
            </Tooltip>
          )
        }
      />

      {sparkleState.isLoading && isAbnValid && (
        <SparkleLoader message={sparkleState.message} />
      )}

      {showEntitySkeleton && (
        <img
          src={Constants.assets.signUpAssets.icons.skeletonLoading}
          alt="Loading"
          height="64"
          css={styles.loadingSkeletonGif}
        />
      )}

      {showEntityNameInput && (
        <TextFields.Outlined
          type="text"
          name="entityName"
          label="Entity name"
          css={[sharedStyles.textfield, styles.abnInput]}
          className="fs-mask"
          fullWidth
          value={formik.values.entityName}
          onChange={formik.handleChange}
          onBlur={handleEntityNameBlur}
          disabled={showEntityNameInput}
          error={formik.touched.entityName && Boolean(formik.errors.entityName)}
          helperText={Validators.renderFieldError(
            formik.touched.entityName,
            formik.errors.entityName,
            submitAttempted
          )}
          endIcon={
            <Tooltip
              content={
                <p className="body4" css={styles.tooltipContent}>
                  Your entity name is your official, registered name that shows
                  up on legal documents.
                </p>
              }
              variant="primary"
              css={sharedStyles.tooltip}
              placement="top"
              enterTouchDelay={20}
              leaveTouchDelay={3000}
              maxWidth={240}
              arrow
              dense
            >
              <Icons.InformationCircle />
            </Tooltip>
          }
        />
      )}

      {showTradingSkeleton && (
        <img
          src={Constants.assets.signUpAssets.icons.skeletonLoading}
          alt="Loading"
          height="102"
          css={styles.loadingSkeletonGif}
        />
      )}

      {showTradingNameInput && (
        <TextFields.Outlined
          type="text"
          name="tradingName"
          label="Trading name"
          css={[
            sharedStyles.textfield,
            styles.abnInput,
            showTradingNameInput && aiSuggestedTradingName
              ? styles.hasAISuggestion
              : null,
          ]}
          className="fs-mask"
          fullWidth
          value={formik.values.tradingName}
          onChange={formik.handleChange}
          onBlur={handleTradingNameBlur}
          error={
            formik.touched.tradingName && Boolean(formik.errors.tradingName)
          }
          helperText={Validators.renderFieldError(
            formik.touched.tradingName,
            formik.errors.tradingName,
            submitAttempted
          )}
          endIcon={
            <Tooltip
              content={
                <p className="body4" css={styles.tooltipContent}>
                  Trading name is the name your company carries out its business
                  to the customers.
                </p>
              }
              variant="primary"
              css={sharedStyles.tooltip}
              placement="top"
              enterTouchDelay={20}
              leaveTouchDelay={3000}
              maxWidth={240}
              arrow
              dense
            >
              <Icons.InformationCircle />
            </Tooltip>
          }
        />
      )}

      {showTradingNameInput && aiSuggestedTradingName ? (
        <AISuggestionButtons
          suggestions={[aiSuggestedTradingName]}
          onSelect={handleTradingNameSuggestionSelect}
        />
      ) : null}

      {showIndustrySkeleton && (
        <img
          src={Constants.assets.signUpAssets.icons.skeletonLoading}
          alt="Loading"
          height="102"
          css={styles.loadingSkeletonGif}
        />
      )}

      {showIndustryTypeInput && (
        <TextFields.Select
          name="industryType"
          label="Industry type"
          css={[
            sharedStyles.textFieldSelect,
            showIndustryTypeInput ? styles.hasAISuggestion : null,
          ]}
          options={industryTypeOptions}
          onChange={handleIndustryTypeChange}
          onBlur={formik.handleBlur}
          value={formik.values.industryType}
          fullWidth
          error={
            formik.touched.industryType && Boolean(formik.errors.industryType)
          }
          helperText={Validators.renderFieldError(
            formik.touched.industryType,
            formik.errors.industryType,
            submitAttempted
          )}
        />
      )}

      {showIndustryTypeInput && aiSuggestedIndustryTypes.length > 0 ? (
        <AISuggestionButtons
          suggestions={aiSuggestedIndustryTypes}
          onSelect={handleIndustryTypeSuggestionSelect}
        />
      ) : null}

      <input
        type="hidden"
        name="tradingSince"
        value={
          formik.values.tradingSince
            ? formik.values.tradingSince.toString()
            : ''
        }
      />
    </form>
  );
};

export default BusinessInfoStepModule;
